import { Component, OnInit, forwardRef, Input, Output, EventEmitter, ViewChild, ElementRef } from '@angular/core';
import { NG_VALUE_ACCESSOR, ControlValueAccessor, FormControl } from '@angular/forms';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import * as _ from 'lodash';

@Component({
  selector: 'ihm-autocomplete-large-chips-list',
  templateUrl: './ihm-autocomplete-large-chips-list.component.html',
  styleUrls: ['./ihm-autocomplete-large-chips-list.component.scss'],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => IhmAutocompleteLargeChipsListComponent),
      multi: true
    }
  ]
})
export class IhmAutocompleteLargeChipsListComponent implements OnInit, ControlValueAccessor {
  private static INVALID_INPUT_WARNING = 'The following options you entered were not found in the selection list: ';

  @ViewChild('chipInput',{ static: true}) chipInput: ElementRef;
  @ViewChild('chipListDiv',{ static: true}) chipListDiv: ElementRef;
  @ViewChild('chipListMaxHeight',{ static: true}) chipListMaxHeight: ElementRef;

  @Output() selectionChange = new EventEmitter();

  // Chip List Properties
  @Input() chipsList = [];
  @Input() allowUnknownOptions = false;
  @Input() removable = true;

  // AutoComplete Properties
  @Input() separatorsRegex: RegExp = /[\n,;]+/;
  @Input() inputPlaceholder = 'Search';
  @Input() autoActiveFirstOption = true;
  @Input() enableHighlight = true;
  @Input() displayProperty = 'name';
  @Input() hideSearchInput = false;

  autoCompleteControl: FormControl;
  filteredOptions: Observable<any[]>;

  invalidWarning = '';

  // Data Properties
  @Input()
  optionsList = [
    { id: 1, name: 'Alternative' },
    { id: 2, name: 'Blues' },
    { id: 3, name: 'Classical' },
    { id: 4, name: 'Country' },
    { id: 5, name: 'Dance' },
    { id: 6, name: 'Electronic' },
    { id: 7, name: 'Folk' },
    { id: 8, name: 'Hip Hop' },
    { id: 9, name: 'Indie' },
    { id: 10, name: 'Inspirational' },
    { id: 11, name: 'Jazz' },
    { id: 12, name: 'Latin Music' },
    { id: 13, name: 'New Age' },
    { id: 14, name: 'Opera' },
    { id: 15, name: 'Pop' },
    { id: 16, name: 'R&B' },
    { id: 17, name: 'Soul' },
    { id: 18, name: 'Reggae' },
    { id: 19, name: 'Rock' },
    { id: 20, name: 'World' }
  ];

  constructor() {}

  ngOnInit() {
    this.autoCompleteControl = new FormControl();

    this.filteredOptions = this.autoCompleteControl.valueChanges.pipe(map(value => (value.length ? this.filterOptions(value) : [])));
  }

  private filterOptions(value: string) {
    const optionsListWithoutSelectedChips = _.differenceWith(this.optionsList, this.chipsList, _.isEqual);

    return optionsListWithoutSelectedChips.filter(option => option[this.displayProperty].toLowerCase().indexOf(value.toLowerCase()) !== -1);
  }

  private emitSelectionChange(): void {
    this.propagateChange(this.chipsList);
    this.selectionChange.emit(this.chipsList);
  }

  private clearInput(): void {
    this.chipInput['nativeElement'].blur();
    this.chipInput['nativeElement'].value = '';
    this.chipInput['nativeElement'].focus();
  }

  removeChip(selectedChip) {
    if (this.removable) {
      this.chipsList = _.pull(this.chipsList, selectedChip);
      this.emitAndResetInput();
    }
  }

  onInputEntered(value: string) {
    if (value) {
      const splitValues: string[] = value.split(this.separatorsRegex).map((splitVal: string) => splitVal.trim());

      // AutoComplete should deal with single value selections
      if (splitValues.length === 1 && this.filterOptions(splitValues[0]).length > 0) {
        return;
      }

      const invalidValues: string[] = [];

      splitValues.forEach((inputValue: any) => {
        const definedOption = this.optionsList.find((option: any) => option[this.displayProperty] === inputValue);

        if (definedOption) {
          if (!this.chipsList.includes(definedOption)) {
            this.chipsList.push(definedOption);
          }
          return;
        }

        if (this.allowUnknownOptions) {
          const existingOption = this.chipsList.find((option: any) => option[this.displayProperty] === inputValue);

          if (!existingOption) {
            const newOption: any = {};
            newOption.id = -1;
            newOption[this.displayProperty] = inputValue;

            this.chipsList.push(newOption);
          }
        } else {
          invalidValues.push(inputValue);
        }
      });

      this.emitAndResetInput();

      if (invalidValues.length > 0) {
        this.invalidWarning = IhmAutocompleteLargeChipsListComponent.INVALID_INPUT_WARNING + invalidValues.join(',');
      }
    }
  }

  onOptionSelected(value: any) {
    this.chipsList.push(value);
    this.emitAndResetInput();
  }

  emitAndResetInput() {
    this.invalidWarning = '';
    this.emitSelectionChange();
    this.clearInput();
  }

  writeValue(value: any): void {
    if (value) {
      this.chipsList = value;
    }
  }

  registerOnChange(fn: any): void {
    this.propagateChange = fn;
  }

  registerOnTouched(fn: any): void {}

  propagateChange = (value: any) => {};

  cssIsOverMaxHeight(): boolean {
    return this.chipListDiv.nativeElement.offsetHeight >= this.chipListMaxHeight.nativeElement.offsetHeight;
  }
}
